import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "utils";

import { CardGrid } from "./CardGrid";
import { Skeleton } from "./ui/skeleton";

/**
 * The two states every data surface has and nobody designs: nothing yet, and
 * not yet.
 *
 * Both were bare sentences in this app. An empty state that names the next
 * action is the difference between "broken" and "ready", and a skeleton is what
 * stops a live-updating page flashing its empty state before the first snapshot
 * arrives.
 */
export function EmptyState({
  icon: Icon,
  title,
  children,
  action,
  testId,
  tone = "plain",
}: {
  icon: LucideIcon;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  testId?: string;
  /** "bold" is the dark band the event pages open with, for a state a person is
   *  meant to act on. Errors keep the plain card. */
  tone?: "plain" | "bold";
}) {
  const bold = tone === "bold";
  return (
    <div
      className={
        bold
          ? "flex flex-col items-center rounded-2xl border border-transparent bg-dusk-hero px-6 py-12 text-center text-white wrap-anywhere dark:border-edge dark:border-lit-edge"
          : "flex flex-col items-center rounded-lg border border-border bg-card px-6 py-12 text-center text-card-foreground wrap-anywhere"
      }
      data-testid={testId}
    >
      <span
        className={
          bold
            ? "mb-4 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white"
            : "mb-4 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-secondary text-secondary-foreground"
        }
      >
        <Icon className="h-6 w-6" />
      </span>
      <p
        className={
          bold
            ? "font-display text-2xl font-normal text-white"
            : "font-medium text-foreground"
        }
      >
        {title}
      </p>
      {children && (
        <p
          className={
            bold
              ? "mt-1 max-w-md text-sm text-white/85"
              : "mt-1 max-w-md text-sm text-muted-foreground"
          }
        >
          {children}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/**
 * Placeholder rows shaped like the table they precede.
 *
 * The bars are fractions, not fixed widths. Three fixed bars totalling 24rem
 * plus gaps do not fit a 390px phone, so the skeleton itself overflowed the
 * page — the loading state announced a layout bug the loaded state did not
 * have.
 */
export function LoadingRows({ rows = 4 }: { rows?: number }) {
  return (
    <div
      className="rounded-lg border border-border bg-card p-4"
      aria-busy="true"
    >
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-center gap-4 py-3">
          <Skeleton className="h-4 w-1/4 min-w-12" />
          <Skeleton className="h-4 w-2/5 min-w-16" />
          <Skeleton className="ml-auto h-6 w-1/5 min-w-12 rounded-full" />
        </div>
      ))}
    </div>
  );
}

/** Placeholder cards shaped like the surface they precede. */
export function LoadingCards({
  cards = 2,
  mode = "stack",
  className,
}: {
  cards?: number;
  mode?: "stack" | "grid";
  className?: string;
}) {
  const skeletons = Array.from({ length: cards }, (_, index) => (
    <div key={index} className="rounded-lg border border-border bg-card p-5">
      <div className="flex justify-between gap-4">
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-5 w-1/5 min-w-12" />
      </div>
      <Skeleton className="mt-3 h-4 w-full" />
      <Skeleton className="mt-4 h-9 w-2/5 min-w-24" />
    </div>
  ));

  if (mode === "grid") {
    return (
      <CardGrid className={className} busy>
        {skeletons}
      </CardGrid>
    );
  }

  return (
    <div className={cn("flex flex-col gap-4", className)} aria-busy="true">
      {skeletons}
    </div>
  );
}

/**
 * The whole-screen wait before there is a page to shape a skeleton after:
 * auth still resolving, or the landing route still choosing a destination.
 */
export function FullPageLoading() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-page">
      <p className="text-muted-foreground">Loading…</p>
    </main>
  );
}
